import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';
import * as C from '../../theme/colors.constants';
import { calcularLimitesRatio } from './calcularLimitesRatio';

const RATIO_STEP = 0.05;

/**
 * Divisor arrastável entre o grid do calendário e a lista de eventos do dia
 * (`CalendarioCard.jsx`). Mesmo padrão de interação de
 * `Processos/ResizeHandle.jsx` (listeners no document gateados por
 * `arrastando`, user-select:none durante o arraste, setas do teclado),
 * mas trabalha em proporção (0-1) em vez de largura em px, porque este
 * card é fluido — sem largura total fixa. Os limites de proporção são
 * recalculados a cada interação a partir da largura real do container
 * (`containerRef`), então continuam corretos se a janela for redimensionada.
 */
export default function CalendarioResizeHandle({
  containerRef, ratio, onChange, minLeftPx, minRightPx, defaultRatio,
}) {
  const [arrastando, setArrastando] = useState(false);

  const calcularLimites = useCallback(() => {
    const largura = containerRef.current ? containerRef.current.getBoundingClientRect().width : 0;
    return calcularLimitesRatio(largura, minLeftPx, minRightPx);
  }, [containerRef, minLeftPx, minRightPx]);

  const aoMover = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const bruto = (e.clientX - rect.left) / rect.width;
    const { min, max } = calcularLimites();
    onChange(Math.min(Math.max(bruto, min), max));
  }, [containerRef, calcularLimites, onChange]);

  const aoSoltar = useCallback(() => setArrastando(false), []);

  useEffect(() => {
    if (!arrastando) return undefined;
    document.addEventListener('mousemove', aoMover);
    document.addEventListener('mouseup', aoSoltar);
    const bodyStyle = document.body.style;
    const anterior = { userSelect: bodyStyle.userSelect, cursor: bodyStyle.cursor };
    bodyStyle.userSelect = 'none';
    bodyStyle.cursor = 'col-resize';
    return () => {
      document.removeEventListener('mousemove', aoMover);
      document.removeEventListener('mouseup', aoSoltar);
      bodyStyle.userSelect = anterior.userSelect;
      bodyStyle.cursor = anterior.cursor;
    };
  }, [arrastando, aoMover, aoSoltar]);

  const iniciar = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setArrastando(true);
  };

  const alternar = () => {
    const { min, max } = calcularLimites();
    onChange(ratio > defaultRatio ? min : max);
  };

  const moverComTeclado = (e) => {
    const { min, max } = calcularLimites();
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange(Math.min(max, ratio + RATIO_STEP));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(Math.max(min, ratio - RATIO_STEP));
    }
  };

  const { min: limiteMin, max: limiteMax } = calcularLimites();

  return (
    <Tooltip title="Arraste para redimensionar" arrow placement="right" disableInteractive>
      <Box
        role="slider"
        aria-orientation="vertical"
        aria-label="Redimensionar calendário e lista de eventos"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(limiteMin * 100)}
        aria-valuemax={Math.round(limiteMax * 100)}
        tabIndex={0}
        onMouseDown={iniciar}
        onDoubleClick={alternar}
        onKeyDown={moverComTeclado}
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 16px',
          alignSelf: 'stretch',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'col-resize',
          '&:focus-visible': { outline: `2px solid ${C.navy}`, borderRadius: 2 },
        }}
      >
        <Box
          sx={{
            width: 3,
            height: '70%',
            borderRadius: 2,
            backgroundColor: arrastando ? C.navy : C.borderGrey,
            transition: 'background-color .15s ease',
          }}
        />
      </Box>
    </Tooltip>
  );
}

CalendarioResizeHandle.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  ratio: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  minLeftPx: PropTypes.number.isRequired,
  minRightPx: PropTypes.number.isRequired,
  defaultRatio: PropTypes.number.isRequired,
};
